import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Outlet } from "react-router-dom";

import SellerSidebar from "../components/layout/SellerSidebar";
import SellerTopbar, {
  SellerNotification,
} from "../components/layout/SellerTopbar";
import MobileWebNotice from "../components/layout/MobileWebNotice";
import NotificationPermissionNotice from "../components/notifications/NotificationPermissionNotice";

import { useAuth } from "../context/AuthContext";
import { useIsMobileViewport } from "../hooks/useIsMobileViewport";
import { registerAdminWebPush } from "../lib/webPush";

const API_URL = "https://api.wynflex.com.ar";

const shellStyle: React.CSSProperties = {
  minHeight: "100vh",
  background: "var(--background)",
  color: "var(--text-primary)",
  display: "flex",
};

const contentShellStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
};

const mainStyle: React.CSSProperties = {
  flex: 1,
  padding: "var(--page-padding)",
};

const innerContentStyle: React.CSSProperties = {
  width: "100%",
};

function normalizeApiNotification(item: any): SellerNotification {
  return {
    id: String(
      item.id ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    ),
    title: item.title || "Lynx",
    body: item.body || item.message || "Nueva notificación",
    data: item.data || {},
    receivedAt:
      item.created_at ||
      item.createdAt ||
      item.received_at ||
      item.receivedAt ||
      new Date().toISOString(),
    read: Boolean(item.read || item.seen || item.is_read),
  };
}

function normalizePushPayload(payload: any): SellerNotification {
  const notification =
    payload?.notification || payload?.message || payload || {};

  return {
    id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
    title: notification.title || "Lynx",
    body: notification.body || "Nueva notificación",
    data: notification.data || {},
    receivedAt: notification.receivedAt || new Date().toISOString(),
    read: false,
  };
}

const SellerLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [notifications, setNotifications] = useState<SellerNotification[]>([]);

  const { token, userId } = useAuth();
  const isMobile = useIsMobileViewport();

  const unreadNotificationsCount = useMemo(
    () => notifications.filter((notification) => !notification.read).length,
    [notifications],
  );

  const handleToggleSidebar = useCallback(() => {
    setCollapsed((prev) => !prev);
  }, []);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/v2/notifications/my`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        console.error(
          "[Lynx Seller Notifications] Error cargando notificaciones",
          response.status,
        );
        return;
      }

      const data = await response.json();

      const rawNotifications = Array.isArray(data)
        ? data
        : data.notifications || data.data || [];

      const normalizedNotifications = rawNotifications
        .map(normalizeApiNotification)
        .slice(0, 50);

      setNotifications(normalizedNotifications);
    } catch (error) {
      console.error(
        "[Lynx Seller Notifications] Error cargando notificaciones",
        error,
      );
    }
  }, [token]);

  const addNotification = useCallback(
    (notification: SellerNotification) => {
      setNotifications((prev) => {
        const exists = prev.some((item) => item.id === notification.id);

        if (exists) return prev;

        return [notification, ...prev].slice(0, 50);
      });
    },
    [],
  );

  const markNotificationAsRead = useCallback(
    async (notificationId: string) => {
      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? { ...notification, read: true }
            : notification,
        ),
      );

      if (!token) return;

      try {
        const response = await fetch(`${API_URL}/v2/notifications/seen`, {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ids: [notificationId],
          }),
        });

        if (!response.ok) {
          fetchNotifications();
        }
      } catch (error) {
        console.error(
          "[Lynx Seller Notifications] Error marcando notificación como leída",
          error,
        );
        fetchNotifications();
      }
    },
    [fetchNotifications, token],
  );

  const markAllNotificationsAsRead = useCallback(async () => {
    setNotifications((prev) =>
      prev.map((notification) => ({
        ...notification,
        read: true,
      })),
    );

    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/v2/notifications/seen`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        fetchNotifications();
      }
    } catch (error) {
      console.error(
        "[Lynx Seller Notifications] Error marcando todas como leídas",
        error,
      );
      fetchNotifications();
    }
  }, [fetchNotifications, token]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    registerAdminWebPush({ token, userId }).catch((error) => {
      console.error(
        "[Lynx Seller Push] Error inicializando web push",
        error,
      );
    });
  }, [token, userId]);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data?.type !== "LYNX_PUSH_RECEIVED") return;

      console.log(
        "[Lynx Seller Push] Notificación recibida",
        event.data.payload,
      );

      addNotification(normalizePushPayload(event.data.payload));
    };

    navigator.serviceWorker.addEventListener(
      "message",
      handleServiceWorkerMessage,
    );

    return () => {
      navigator.serviceWorker.removeEventListener(
        "message",
        handleServiceWorkerMessage,
      );
    };
  }, [addNotification]);

  if (isMobile) {
    return <MobileWebNotice />;
  }

  return (
    <div style={shellStyle}>
      <SellerSidebar collapsed={collapsed} />

      <div style={contentShellStyle}>
        <SellerTopbar
          collapsed={collapsed}
          onToggleSidebar={handleToggleSidebar}
          notifications={notifications}
          unreadNotificationsCount={unreadNotificationsCount}
          onMarkNotificationAsRead={markNotificationAsRead}
          onClearNotifications={markAllNotificationsAsRead}
        />

        <NotificationPermissionNotice />

        <main style={mainStyle}>
          <div style={innerContentStyle}>
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default SellerLayout;